// Utility functions for font management

import { IFontFamily } from '@/interfaces/editor'
import groupBy from 'lodash/groupBy'
import { FontGroup, FontPack } from './types'

// Cache for user languages to avoid repeated access
let cachedUserLanguages: string[] | null = null
let lastStoredLang: string | null = null

// Get user languages from localStorage and navigator (cached)
export const getUserLanguages = (): string[] => {
  const currentStoredLang = typeof localStorage !== 'undefined' 
    ? localStorage.getItem('language') 
    : null
  
  // Return cached value if stored language hasn't changed
  if (cachedUserLanguages && currentStoredLang === lastStoredLang) {
    return cachedUserLanguages
  }
  
  const languages: string[] = []
  
  if (currentStoredLang) languages.push(currentStoredLang)
  
  if (typeof navigator !== 'undefined') {
    if (navigator.languages) languages.push(...navigator.languages)
    if (navigator.language) languages.push(navigator.language)
  }
  
  cachedUserLanguages = [...new Set(languages)]
  lastStoredLang = currentStoredLang
  
  return cachedUserLanguages
}

// Clear language cache (useful for testing or when language changes)
export const clearLanguageCache = () => {
  cachedUserLanguages = null
  lastStoredLang = null
}

// Filter fonts by locale matching
export const filterFontsByLocale = (
  fontPacks: FontPack[],
  userLanguages: readonly string[]
): FontPack[] => {
  if (userLanguages.length === 0) {
    return fontPacks
  }
  return fontPacks.filter(
    (font) =>
      !font.locale || userLanguages.some(lang => lang.startsWith(font.locale!))
  )
}

// Shared helper to build font groups from fonts and font packs
// This avoids duplicate logic in loadLanguageSpecificFonts and loadFontsWithGroups
export const buildFontGroups = (
  allFonts: IFontFamily[],
  filteredFontPacks: FontPack[]
): FontGroup => {
  const fontsGroupedByCategory = groupBy(allFonts, 'category')

  // Create category mapping with is_free flag
  const categoryMap = filteredFontPacks.map(f => ({
    name: f.name,
    is_free: f.is_free,
  }))

  // Build font groups
  const fontGroups: FontGroup = {}
  categoryMap.forEach(c => {
    if (fontsGroupedByCategory[c.name]) {
      fontGroups[c.name] = fontsGroupedByCategory[c.name].map(f => ({
        ...f,
        is_free: c.is_free,
      }))
    }
  })

  return fontGroups
}
